import React from 'react';
import {
  Box,
  Typography,
  IconButton,
  Button
} from '@mui/material';
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import CloseIcon from '@mui/icons-material/Close';

/**
 * StandardActiveFilters Component
 * 
 * Displays a list of currently active filters with the ability to remove individual filters or clear all.
 * The component provides an interactive interface for managing applied filters with hover effects and animations.
 * 
 * Features:
 * - Displays active filters as interactive chips
 * - Individual filter removal
 * - Clear all filters option
 * - Hover effects and animations
 * - Responsive design
 * 
 * @component
 * @param {Object} props
 * @param {Object} props.activeFilters - Object containing active filters where key is field name and value is filter value
 * @param {Array} props.filterableColumns - Array of column definitions that can be filtered
 *   @param {string} field - Unique identifier for the column
 *   @param {string} label - Display label for the column
 *   @param {string} [filterType] - Type of filter ('select' or 'text')
 *   @param {Array} [filterOptions] - Options for select-type filters
 * @param {function} props.onFilterClick - Callback when a filter chip is clicked
 * @param {function} props.onFilterClear - Callback when individual filter is cleared
 * @param {function} props.onFilterClearAll - Callback when all filters are cleared
 * 
 * @example
 * ```jsx
 * const activeFilters = {
 *   status: 'active',
 *   category: 'electronics'
 * };
 * 
 * const filterableColumns = [
 *   { field: 'status', label: 'Status' },
 *   { field: 'category', label: 'Category' }
 * ];
 * 
 * <StandardActiveFilters
 *   activeFilters={activeFilters}
 *   filterableColumns={filterableColumns}
 *   onFilterClick={(e, field) => console.log('Filter clicked:', field)}
 *   onFilterClear={(field) => console.log('Clear filter:', field)}
 *   onFilterClearAll={() => console.log('Clear all filters')}
 * />
 * ```
 */

const ActiveFilters = ({
  activeFilters,
  filterableColumns,
  onFilterClick,
  onFilterClear,
  onFilterClearAll
}) => {
  if (Object.keys(activeFilters).length === 0) return null;

  const renderFilterValue = (column, value) => {
    if (column.filterType === 'multi-select' && Array.isArray(value)) {
      return value.map(v => {
        const option = column.filterOptions?.find(opt => opt.value === v);
        return option?.label || v;
      }).join(', ');
    } else if (column.filterType === 'select') {
      const option = column.filterOptions?.find(opt => opt.value === value);
      return option?.label || value;
    }
    return value;
  };

  return (
    <Box 
      sx={{ 
        width: '100%',
        padding: '3px 16px',
        backgroundColor: '#FFFFFF'
      }}
    >
      <Box 
        sx={{ 
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1,
          width: '100%'
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center',
          color: '#6B7280',
          mr: 1
        }}>
          <FilterAltOutlinedIcon sx={{ fontSize: 16, mr: 0.5 }} />
          <Typography sx={{ fontSize: '12px', fontWeight: 500 }}>
            Active Filters:
          </Typography>
        </Box>
        {Object.entries(activeFilters).map(([field, value]) => {
          const column = filterableColumns?.find(col => col.field === field);
          if (!column) return null;

          return (
            <Box
              key={field}
              onClick={(e) => onFilterClick(e, field)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                backgroundColor: '#F3F4F6',
                borderRadius: '6px',
                padding: '4px 8px',
                gap: 1,
                cursor: 'pointer',
                border: '1px solid transparent',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: '#EBEEFF',
                  borderColor: '#1D3DFC20',
                  '& .filter-label': {
                    color: '#1D3DFC'
                  },
                  '& .filter-value': {
                    color: '#1D3DFC'
                  },
                  '& .clear-button': {
                    opacity: 1,
                    color: '#1D3DFC'
                  }
                }
              }}
            >
              <Typography 
                className="filter-label"
                sx={{ 
                  fontSize: '12px',
                  color: '#6B7280',
                  mr: 0.5,
                  transition: 'color 0.2s'
                }}
              >
                {column.label}:
              </Typography>
              <Typography 
                className="filter-value"
                sx={{ 
                  fontSize: '12px',
                  color: '#111827',
                  fontWeight: 500,
                  transition: 'color 0.2s',
                  maxWidth: '200px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {renderFilterValue(column, value)}
              </Typography>
              <IconButton
                className="clear-button"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onFilterClear(field);
                }}
                sx={{ 
                  color: '#6B7280',
                  p: 0,
                  width: '16px',
                  height: '16px',
                  ml: 0.5,
                  opacity: 0.7,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    color: '#DC2626',
                    backgroundColor: 'transparent'
                  }
                }}
              >
                ×
              </IconButton>
            </Box>
          );
        })}
        <Button
          size="small"
          onClick={onFilterClearAll}
          sx={{
            fontSize: '12px',
            textTransform: 'none',
            color: '#6B7280',
            p: '4px 8px',
            minWidth: 'auto',
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor: 'transparent',
              color: '#DC2626'
            }
          }}
        >
          Clear all
        </Button>
      </Box>
    </Box>
  );
};

export default ActiveFilters; 